import React, { useMemo, useState } from 'react';
import { ArrowCounterClockwise, CircleNotch, CoinVertical, Lightning } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';
import { fmtPLN } from './obligationUtils';

const API = `${window.location.origin}/api`;
const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

// Mirror of backend plan_nadplata: consume from the LAST unpaid installment backwards.
const planNadplata = (payments, kwota) => {
    let left = r2(kwota);
    const unpaid = (payments || []).filter((p) => !p.paid).sort((a, b) => b.payment_number - a.payment_number);
    const changes = [];
    for (const p of unpaid) {
        if (left <= 0) break;
        const amt = r2(p.amount);
        if (amt <= 0) continue;
        const take = Math.min(amt, left);
        changes.push({ payment_number: p.payment_number, date: p.date, before: amt, after: r2(amt - take) });
        left = r2(left - take);
    }
    return { changes, over: left > 0.009 };
};

export default function NadplataPanel({ kind, credits, onDone }) {
    const [creditId, setCreditId] = useState('');
    const [kwota, setKwota] = useState('');
    const [busy, setBusy] = useState(false);
    const credit = credits.find((c) => c.id === creditId);
    const unpaid = (credit?.payments || []).filter((p) => !p.paid);
    const unpaidSum = unpaid.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const amount = parseFloat(String(kwota).replace(',', '.'));
    const plan = useMemo(() => (credit && amount > 0 ? planNadplata(credit.payments, amount) : null), [credit, amount]);
    const history = (credit?.nadplaty || []).slice().reverse();

    const label = (c) => (kind === 'private' ? `${c.employee_name || '?'} — ${c.name}` : c.name);

    const apply = async () => {
        if (!plan || plan.over) return;
        const closed = plan.changes.filter((c) => c.after <= 0).length;
        if (!window.confirm(`Zastosować nadpłatę ${fmtPLN(amount)}?\nZamknięte raty: ${closed}${plan.changes.some((c) => c.after > 0) ? ', jedna zmniejszona' : ''}.`)) return;
        setBusy(true);
        try {
            await axios.post(`${API}/credit-nadplata/${kind}/${creditId}`, { kwota: amount });
            setKwota('');
            await onDone();
        } catch (e) { alert(e.response?.data?.detail || 'Błąd'); }
        finally { setBusy(false); }
    };
    const undo = async (n) => {
        if (!window.confirm(`Cofnąć nadpłatę ${fmtPLN(n.kwota)}? Raty wrócą do poprzednich kwot.`)) return;
        setBusy(true);
        try { await axios.delete(`${API}/credit-nadplata/${kind}/${creditId}/${n.id}`); await onDone(); }
        catch (e) { alert(e.response?.data?.detail || 'Błąd'); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-4" data-testid={`nadplata-panel-${kind}`}>
            <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5 space-y-4">
                <div className="flex items-center gap-2">
                    <CoinVertical className="w-5 h-5 text-violet-600" weight="bold" />
                    <h3 className="font-bold text-slate-800">Nadpłata kredytu</h3>
                </div>
                <p className="text-xs text-slate-500">Kwota nadpłaty zamyka raty od końca harmonogramu. Np. 10 rat po 200 zł, nadpłata 900 zł → 4 ostatnie raty zamknięte, jedna zmniejszona do 100 zł.</p>
                <div className="grid sm:grid-cols-[1fr_200px] gap-3">
                    <select value={creditId} onChange={(e) => { setCreditId(e.target.value); setKwota(''); }} className="input-field" data-testid="nadplata-credit-select">
                        <option value="">— wybierz kredyt —</option>
                        {credits.map((c) => <option key={c.id} value={c.id}>{label(c)}</option>)}
                    </select>
                    <input type="text" inputMode="decimal" value={kwota} onChange={(e) => setKwota(e.target.value)} disabled={!credit} placeholder="Kwota nadpłaty (zł)" className="input-field" data-testid="nadplata-amount-input" />
                </div>
                {credit && (
                    <div className="text-sm text-slate-600" data-testid="nadplata-credit-summary">
                        Pozostało: <b>{unpaid.length}</b> rat, razem <b>{fmtPLN(unpaidSum)}</b>
                    </div>
                )}
                {plan && plan.over && (
                    <div className="text-sm font-semibold text-red-600" data-testid="nadplata-over-error">Nadpłata jest większa niż pozostało do spłaty ({fmtPLN(unpaidSum)}).</div>
                )}
                {plan && !plan.over && plan.changes.length > 0 && (
                    <div className="border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="nadplata-preview">
                        {plan.changes.slice().sort((a, b) => a.payment_number - b.payment_number).map((c) => (
                            <div key={c.payment_number} className="flex items-center justify-between px-3 py-2 text-sm">
                                <span className="text-slate-700">Rata {c.payment_number} · {c.date}</span>
                                <span>
                                    <span className="line-through text-slate-400 mr-2">{fmtPLN(c.before)}</span>
                                    {c.after <= 0
                                        ? <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">Nadpłata · 0 zł</span>
                                        : <b className="text-slate-800">{fmtPLN(c.after)}</b>}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
                <button onClick={apply} disabled={busy || !plan || plan.over || plan.changes.length === 0} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="nadplata-apply-btn">
                    {busy ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Lightning className="w-4 h-4" weight="bold" />} Zastosuj nadpłatę
                </button>
            </div>

            {credit && history.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="nadplata-history">
                    <h4 className="font-semibold text-sm text-slate-800 mb-2">Historia nadpłat</h4>
                    <div className="divide-y divide-slate-100">
                        {history.map((n, i) => (
                            <div key={n.id} className="flex items-center justify-between py-2 text-sm" data-testid={`nadplata-history-${n.id}`}>
                                <span className="text-slate-700">{n.data} · <b>{fmtPLN(n.kwota)}</b> · zamknięte raty: {(n.changes || []).filter((c) => c.after <= 0).length}</span>
                                {i === 0 && (
                                    <button onClick={() => undo(n)} disabled={busy} className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline" data-testid={`nadplata-undo-${n.id}`}>
                                        <ArrowCounterClockwise className="w-3.5 h-3.5" /> Cofnij
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
